import type { Prisma } from '@prisma/client';

export interface ImportColumn {
  key: string;
  label: string;
  required?: boolean;
  example: string;
  /** Alternative header names recognised when auto-matching. */
  aliases?: string[];
}

export interface ImportOptions {
  /** What to do when a row matches an existing record. */
  duplicate: 'skip' | 'update' | 'fail';
  /** What to do when a row references a record that does not exist (e.g. an unknown client). */
  missingRef: { mode: 'error' | 'create' | 'map'; map?: Record<string, number> };
  /** Financial documents: import as Approved (posts to the ledger) instead of Draft. Needs approve permission. */
  asApproved?: boolean;
}

export const DEFAULT_OPTIONS: ImportOptions = { duplicate: 'skip', missingRef: { mode: 'error' } };

export interface ImportContext {
  tx: Prisma.TransactionClient;
  companyId: number;
  userId: number;
  options: ImportOptions;
  /** Per-run scratch space (e.g. created reference ids) so repeated names resolve to one record. */
  cache: Map<string, unknown>;
}

export interface MissingRef {
  kind: string;
  value: string;
  /** Extra context for creating the record (e.g. an item's description and price). */
  hint?: string;
}

export type ParsedRow<T> = { ok: true; value: T } | { ok: false; errors: string[] };

/** One importable record type. Later phases register invoices, receipts and so on the same way. */
export interface ImportEntity<T = Record<string, unknown>> {
  key: string;
  label: string;
  /** Permission needed to import and to download the template. */
  importPermission: string;
  columns: ImportColumn[];
  /**
   * Documents with line items use one CSV row per line, grouped by this column (e.g. invoice_number).
   * Rows sharing the value form ONE record and are parsed together by `parseGroup`.
   */
  groupKey?: string;
  parseGroup?(rows: { rowNumber: number; raw: Record<string, string> }[]): ParsedRow<T>;
  /** Validate and normalise one mapped row. Uses the same Zod schemas as the UI. */
  parseRow(raw: Record<string, string>): ParsedRow<T>;
  /** Natural key used to detect duplicates within the file. */
  rowKey(value: T): string | null;
  /** Extra checks that need the database (e.g. an owner email must belong to a user). Return error messages. */
  validate?(ctx: ImportContext, value: T): Promise<string[]>;
  findExisting(ctx: ImportContext, value: T): Promise<{ id: number; snapshot: unknown } | null>;
  missingRefs?(ctx: ImportContext, value: T): Promise<MissingRef[]>;
  /** Called at commit for refs the user chose to create ("Incomplete" records). */
  createRef?(ctx: ImportContext, ref: MissingRef): Promise<number>;
  /** Undo a ref created by createRef (rollback). Return an error message if it is now in use. */
  removeRef?(ctx: ImportContext, ref: { kind: string; id: number }): Promise<string | null>;
  create(ctx: ImportContext, value: T, refs: Map<string, number>): Promise<{ id: number }>;
  update(ctx: ImportContext, id: number, value: T, refs: Map<string, number>): Promise<void>;
  /** Put an updated record back the way it was (batch rollback). */
  restore(ctx: ImportContext, id: number, before: unknown): Promise<void>;
  /** Undo a created record. Return an error message if it can no longer be removed safely. */
  remove(ctx: ImportContext, id: number): Promise<string | null>;
}

const registry = new Map<string, ImportEntity<never>>();
export const registerImportEntity = <T>(e: ImportEntity<T>) =>
  registry.set(e.key, e as unknown as ImportEntity<never>);
export const getImportEntity = (key: string) =>
  registry.get(key) as unknown as ImportEntity | undefined;
export const listImportEntities = () => [...registry.values()] as unknown as ImportEntity[];
